import { useCallback, useState } from "react";
import { trackEvent, TrackedEvent, type RemindersDeliveryOption } from "@/domains/metrics";
import { buildOriginalSubscriptionPlanMetricsPayload } from "@/domains/metrics/monetization";
import { windowModule } from "@/modules/window";
import { useAppStore } from "@/store";

/**
 * Coordinates Reminders enable clicks, analytics, Slack setup, and unavailable connector state.
 *
 * Slack access is refreshed before an upsell so a just-completed paid upgrade is not sent
 * back through checkout. An explicit retry also replaces OAuth state left by an abandoned handoff.
 */
export const useRemindersEnableActions = () => {
  const { store } = useAppStore();
  const { memAgent } = store;
  const [isStartingSlackConnection, setIsStartingSlackConnection] = useState(false);

  const userEmail = store.account.myAccount.profileEmailAddress;
  const settingsSource = store.settingsModal.memAgentSettingsSource;
  const isSlackConnected = memAgent.isSlackConnected;
  const requiresAssistantSetup = !memAgent.hasAssistant || !memAgent.isAssistantEnabled;
  const {
    original_plan_kind: originalPlanKind,
    original_effective_plan_kind: originalEffectivePlanKind,
  } = buildOriginalSubscriptionPlanMetricsPayload({ store });

  const openSlack = useCallback(() => {
    const slackUrl = memAgent.slackAppUrl ?? memAgent.slackWorkspaceUrl;

    trackEvent(TrackedEvent.MemAgentSlackOpenClick, {
      source: "settings",
      slack_workspace_id: memAgent.slackWorkspaceId,
      slack_team_name: memAgent.slackTeamName,
    });

    if (slackUrl) {
      windowModule.openInNewTab({ url: new URL(slackUrl) });
      return;
    }

    const workspacePath = memAgent.slackWorkspaceId ? `/${memAgent.slackWorkspaceId}` : "";
    windowModule.openInNewTab({ url: new URL(`https://app.slack.com/client${workspacePath}`) });
  }, [memAgent]);

  const enableSlack = useCallback(async () => {
    if (isStartingSlackConnection) {
      return;
    }

    if (isSlackConnected) {
      openSlack();
      return;
    }

    setIsStartingSlackConnection(true);

    try {
      if (!store.subscriptions.hasAgentChannelAccess) {
        await store.platformInfo.fetchPlatformInfo({ reconcileStripeSubscription: true });

        if (!store.subscriptions.hasAgentChannelAccess) {
          store.subscriptions.openProactiveUpsell();
          return;
        }
      }

      if (memAgent.hasPendingSlackOAuthContext) {
        memAgent.clearPendingSlackOAuthContext();
      }

      if (requiresAssistantSetup) {
        await memAgent.setup();
      }

      await memAgent.connectSlack({
        source: "settings",
        isSetup: requiresAssistantSetup,
        originalPlanKind,
        originalEffectivePlanKind,
      });
    } finally {
      setIsStartingSlackConnection(false);
    }
  }, [
    isStartingSlackConnection,
    isSlackConnected,
    memAgent,
    openSlack,
    originalEffectivePlanKind,
    originalPlanKind,
    requiresAssistantSetup,
    store.platformInfo,
    store.subscriptions,
  ]);

  const enableOption = useCallback(
    (option: RemindersDeliveryOption) => {
      trackEvent(TrackedEvent.RemindersEnableClicked, {
        source: settingsSource,
        user_email: userEmail,
        option,
      });

      if (option !== "Slack") {
        memAgent.openRemindersComingSoonModal(option);
        return;
      }

      void enableSlack();
    },
    [enableSlack, memAgent, settingsSource, userEmail]
  );

  return {
    enableOption,
    isSlackConnected,
    isEnablingSlack:
      isStartingSlackConnection || memAgent.isConnectingSlack || memAgent.isSettingUp,
    openSlack,
  };
};
